import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { LegalEntity } from '../../objects/legal-entity';
import { BehaviorSubject, Observable, catchError, map, tap, of, filter, throwError, take, switchMap } from 'rxjs';
import { PaginationService } from '../pagination.service';

@Injectable({
  providedIn: 'root'
})
export class LegalEntityService {

  private http = inject(HttpClient);
  private paginationService = inject(PaginationService);

  private _allLegalEntitiesSubject = new BehaviorSubject<LegalEntity[]>([]);
  public allLegalEntities$ = this._allLegalEntitiesSubject.asObservable();
  private isCacheRefreshedSubject = new BehaviorSubject<boolean>(false);
  public isCacheRefreshed$ = this.isCacheRefreshedSubject.asObservable();

  constructor() {
    // Refresh the cache as soon as the service is instantiated
    this.refreshAllLegalEntitiesRecords().subscribe({
      next: () => this.isCacheRefreshedSubject.next(true),
      error: (err) => {
        console.error('Initial legal entity cache refresh failed.', err);
        this.isCacheRefreshedSubject.next(false);
      }
    });
  }

  /**
   * Writes a legal entity to the database.
   * This is now a reactive function that returns an Observable.
   * Upon a successful write, it automatically triggers a data refresh.
   * @param legalEntityObject The legal entity to write.
   * @returns An Observable of the API response.
   */
  public writeLegalEntity(legalEntityObject: LegalEntity): Observable<any> {
    const jsonReadyBody = {
      'legalEntityData': legalEntityObject,
      'legalEntityNum': legalEntityObject.legalEntityNum
    };

    const options = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    return this.http.put(environment.legalEntityWriteUrl, jsonReadyBody, options)
      .pipe(
        // tap(() => console.log('Successfully wrote legal entity to database.')),
        tap(() => {
          // Refresh cache after writing
          this.refreshAllLegalEntitiesRecords().subscribe();
        }),
        catchError(error => {
          console.error('An error occurred while writing legal entity:', error);
          return throwError(() => new Error('Could not write legal entity to the database.'));
        })
      );
  }



  /**
   * Gets a specific legal entity object from the in-memory cache based on its ID or name.
   * @param entityIdOrName The unique ID or name of the legal entity.
   * @returns An observable that emits the legal entity object or null if not found.
   */
  public getLegalEntityObject(entityIdOrName: string): Observable<LegalEntity | null> {
    return this.allLegalEntities$.pipe(
      map(legalEntities => {
        const foundEntity = legalEntities.find(entity => 
          entity.legalEntityNum === entityIdOrName || entity.legalEntityName === entityIdOrName
        );
        return foundEntity || null;
      }),
      take(1)
    );
  }

  /**
   * Gets a specific legal entity friendly name from the in-memory cache based on its ID.
   * @param entityId The unique ID of the legal entity.
   * @returns An observable that emits the legal entity's friendly name.
   */
  public getLegalEntityFriendlyName(entityId: string): Observable<string | null> {
    return this.allLegalEntities$.pipe(
      map(legalEntities => {
        const foundEntity = legalEntities.find(entity => entity.legalEntityNum === entityId);
        return foundEntity?.legalEntityName ?? null;
      }),
      take(1)
    );
  }

  /**
   * Gets a fully-hydrated Legal Entity object by its ID.
   * @param entityId The ID of the Legal Entity to retrieve.
   * @returns An observable of the fully-hydrated Legal Entity or null if not found.
   */
  public getLegalEntityObjectFullyHydrated(entityId: string): Observable<LegalEntity | null> {
    return this.isCacheRefreshed$.pipe(
      filter(isRefreshed => isRefreshed),
      take(1),
      switchMap(() => this.getLegalEntityObject(entityId)),
      switchMap(legalEntity => {
        if (!legalEntity) {
          return of(null);
        }
        // Future: Add hydration logic here when connections are ready
        // return this.connectionsService.hydrateEntity(legalEntity, propertyMap);
        return of(legalEntity); // Returning the un-hydrated legal entity for now
      }),
      catchError(error => {
        console.error('Error fetching Legal Entity object:', error);
        return of(null);
      })
    );
  }

  /**
   * Refreshes all legal entity records by progressively fetching pages
   * from the API and updating the BehaviorSubject.
   * @returns An observable of the refreshed legal entities array.
   */
  public refreshAllLegalEntitiesRecords(): Observable<LegalEntity[]> {
    return this.paginationService.getAllPaginatedDataProgressively(key => this.getLegalEntitiesByPagination(key))
      .pipe(
        // Process the legal entities similar to PartyService pattern
        map(rawLegalEntities => {
          const processedEntities: LegalEntity[] = [];
          
          rawLegalEntities.forEach((entityResponse: any) => {
            // Handle both nested data structure (like PartyService) and flat structure
            let entityData: LegalEntity;
            
            if (entityResponse.legalEntityData) {
              // Nested structure
              entityData = { ...entityResponse.legalEntityData };
            } else {
              // Flat structure
              entityData = entityResponse as LegalEntity;
            }
            
            // Mark inactive entities
            if (entityData.inactive) {
              entityData.legalEntityName = `${entityData.legalEntityName} (INACTIVE)`;
            }
            
            processedEntities.push(entityData);
          });
          
          return processedEntities;
        }),
        // tap(allLegalEntities => console.log('Successfully fetched all legal entities:', allLegalEntities)),
        tap(allLegalEntities => this._allLegalEntitiesSubject.next(allLegalEntities)),
        catchError(error => {
          console.error('Error refreshing legal entities:', error);
          this._allLegalEntitiesSubject.next([]);
          return throwError(() => error);
        })
      );
  }

  /**
   * Forces the allLegalEntities$ observable to refresh its data from the API.
   * Components or services can call this method to invalidate the cache.
   */
  public refreshAllLegalEntitiesCache(): void {
    this.refreshAllLegalEntitiesRecords().subscribe();
  }

  /**
   * Modern reactive function to save a new simplified legal entity.
   * @param entityName The name of the new legal entity.
   * @returns An observable of the new legal entity object from the database.
   */
  public saveNewLegalEntitySimplified(entityName: string | null): Observable<LegalEntity> {
    if (!entityName) {
      console.warn('Cannot save a legal entity with a null name. Bailing out.');
      return of({} as LegalEntity);
    }

    const newEntityData: LegalEntity = { 
      legalEntityName: entityName,
      // Add other required LegalEntity properties as needed
    } as LegalEntity;
    
    return this.writeLegalEntity(newEntityData).pipe(
      tap(() => {
        // console.log('New legal entity saved. Legal entity cache is now being refreshed.');
      }),
      catchError(error => {
        console.error('Failed to save new legal entity:', error);
        return throwError(() => new Error('Could not save new legal entity.'));
      })
    );
  }

  /**
   * Gets active legal entities only (excludes inactive entities).
   * @returns An observable of active legal entities.
   */
  public getActiveLegalEntities(): Observable<LegalEntity[]> {
    return this.allLegalEntities$.pipe(
      map(entities => entities.filter(entity => !entity.inactive))
    );
  }

  /**
   * Gets inactive legal entities only.
   * @returns An observable of inactive legal entities.
   */
  public getInactiveLegalEntities(): Observable<LegalEntity[]> {
    return this.allLegalEntities$.pipe(
      map(entities => entities.filter(entity => entity.inactive))
    );
  }

  /**
   * Searches legal entities by name (case-insensitive partial match).
   * @param searchTerm The term to search for.
   * @returns An observable of matching legal entities.
   */
  public searchLegalEntitiesByName(searchTerm: string): Observable<LegalEntity[]> {
    if (!searchTerm) {
      return this.allLegalEntities$;
    }

    return this.allLegalEntities$.pipe(
      map(entities => 
        entities.filter(entity => 
          entity.legalEntityName?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    );
  }

  /**
   * Fetches a single page of legal entities. This private helper method now returns
   * the structured response that directly matches the updated pagination service.
   * @param lastEvaluatedKey The key for the next page of results.
   */
  private getLegalEntitiesByPagination(lastEvaluatedKey: any = null): Observable<any> {
    const body = {
      'lastEvaluatedKey': lastEvaluatedKey
    };

    const options = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    return this.http.post(environment.URL_LEGAL_ENTITIES_GET_PAGE, body, options).pipe(
      // tap(response => console.log('Fetched legal entity page:', response)),
      // Handle both response formats for flexibility
      map((response: any) => {
        // If response already has correct structure, return as-is
        if (response.items && response.hasOwnProperty('lastEvaluatedKey')) {
          return response;
        }
        
        // Otherwise, transform to expected structure
        return {
          items: response.legalEntities || response.items || [],
          lastEvaluatedKey: response.lastEvaluatedKey
        };
      }),
      catchError(error => {
        console.error('Error fetching a page of legal entities:', error);
        return throwError(() => error);
      })
    );
  }
}